import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import {
  BackofficeActivitiesOrderItem,
  BackofficeActivitiesOrderItemWithVoucher,
  VOUCHER_REWARD_TYPE,
} from '@diamond/shared/types';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import {
  getOrderDetailProductDisplay,
  ProductWithPromo,
} from '@diamond/sol-buyer/utils';
import { dinero, toDecimal } from 'dinero.js';

import { VoucherInfo } from '../types/activity-column-types';

export function formatQuantity(item: BackofficeActivitiesOrderItem): number {
  const rawQuantity =
    item.quantity_free_goods && Number(item.quantity_free_goods) > 0
      ? Number(item.quantity_free_goods)
      : item.quantity !== undefined
      ? Number(item.quantity)
      : 0;

  const sanitizedQuantity = Number.isFinite(rawQuantity)
    ? Math.trunc(rawQuantity)
    : 0;

  return Number(
    toDecimal(
      dinero({
        amount: sanitizedQuantity,
        currency: STOCK_UI,
      })
    )
  );
}

export function calculateVoucherDiscount(
  price: number,
  voucherInfo: VoucherInfo
): { finalPrice: number; originalPrice: number; discountAmount?: number } {
  const originalPrice = price;
  let finalPrice = price;

  if (!voucherInfo.hasVoucherDiscount) {
    return { finalPrice, originalPrice };
  }

  if (voucherInfo.voucherDiscountPercentage) {
    const voucherDiscount =
      (finalPrice * voucherInfo.voucherDiscountPercentage) / 100;
    finalPrice = finalPrice - voucherDiscount;

    return {
      finalPrice,
      originalPrice,
      discountAmount: voucherInfo.voucherDiscountPercentage * 100,
    };
  }

  if (voucherInfo.voucherDiscountAmount) {
    finalPrice = finalPrice - voucherInfo.voucherDiscountAmount;

    return {
      finalPrice,
      originalPrice,
    };
  }

  return { finalPrice, originalPrice };
}

export type OrderItemUnitPriceFields = {
  price?: number;
  latest_price?: number | null;
  is_discount?: boolean;
  price_after_discount?: number | null;
  quantity_free_goods?: number | null;
  has_voucher_discount?: boolean;
  voucher_discount_percentage?: number;
  voucher_discount_amount?: number;
  checkout_unit_price?: number | null;
  checkout_original_unit_price?: number | null;
  processed_unit_price?: number | null;
  processed_original_unit_price?: number | null;
  delivered_unit_price?: number | null;
  delivered_original_unit_price?: number | null;
  received_unit_price?: number | null;
  received_original_unit_price?: number | null;
};

export function resolveOrderItemUnitPriceBase(
  item: Pick<OrderItemUnitPriceFields, 'price' | 'latest_price'>
): number {
  return item.latest_price ?? item.price ?? 0;
}

export function getPromoAdjustedUnitPrice(
  item: Pick<
    OrderItemUnitPriceFields,
    'price' | 'latest_price' | 'is_discount' | 'price_after_discount'
  >
): number {
  const unitPriceBase = resolveOrderItemUnitPriceBase(item);

  if (item.is_discount) {
    return item.price_after_discount ?? unitPriceBase;
  }

  return unitPriceBase;
}

/**
 * Checkout-time base price. Prefers the checkout_original_unit_price snapshot
 * (populated for orders processed through the fulfillment-snapshot system);
 * falls back to item.price for legacy orders that predate that snapshot.
 *
 * NOTE: as of this writing, readActivityDetail's order_items SELECT
 * (libs/api/features/backoffice/order/src/lib/data.ts) does not include
 * checkout_original_unit_price/checkout_unit_price/checkout_quantity/
 * checkout_subtotal, so these preferred branches are currently inert and
 * every row falls through to item.price. Buyer's getProductOrderItem
 * (libs/api/features/order/src/lib/data.ts) already selects the same
 * columns — once the backend adds them here too, this resolves correctly
 * with no frontend changes needed.
 */
export function resolveOrderItemCheckoutPriceBase(
  item: Pick<OrderItemUnitPriceFields, 'price' | 'checkout_original_unit_price'>
): number {
  return item.checkout_original_unit_price ?? item.price ?? 0;
}

/**
 * Pre-discount list price for the Harga Checkout strikethrough row.
 * Prefers checkout_original_unit_price; for legacy rows falls back to latest_price
 * because readActivityDetail recomputes price_after_discount from latest_price
 * (populatePriceAfterDiscountForSingleProduct), matching buyer OrderDetailPromoPrice
 * which also strikethroughs latest_price ?? price.
 */
export function resolveOrderItemCheckoutStrikethroughBase(
  item: Pick<
    OrderItemUnitPriceFields,
    'price' | 'latest_price' | 'checkout_original_unit_price'
  >
): number {
  return (
    item.checkout_original_unit_price ?? item.latest_price ?? item.price ?? 0
  );
}

/** Checkout-time promo-adjusted price — mirrors getPromoAdjustedUnitPrice but anchored to the checkout base price. */
export function getPromoAdjustedCheckoutUnitPrice(
  item: Pick<
    OrderItemUnitPriceFields,
    'price' | 'checkout_original_unit_price' | 'is_discount' | 'price_after_discount'
  >
): number {
  const unitPriceBase = resolveOrderItemCheckoutPriceBase(item);

  if (item.is_discount) {
    return item.price_after_discount ?? unitPriceBase;
  }

  return unitPriceBase;
}

export type FulfillmentStage = 'processed' | 'delivered' | 'received';

export type ActivityDetailStageKey = 'checkout' | FulfillmentStage;

const ACTIVITY_DETAIL_STAGE_ORDER: Record<ActivityDetailStageKey, number> = {
  checkout: 0,
  processed: 1,
  delivered: 2,
  received: 3,
};

type FulfillmentStageDataFields = Pick<
  BackofficeActivitiesOrderItem,
  | 'processed_subtotal'
  | 'processed_unit_price'
  | 'delivered_subtotal'
  | 'delivered_unit_price'
  | 'received_subtotal'
  | 'received_unit_price'
>;

const hasStageUnitPriceSnapshot = (
  value: number | null | undefined
): boolean => value != null;

/** Highest fulfillment stage that should show data for a given order.status. */
export function resolveMaxVisibleFulfillmentStage(
  orderStatus: string | undefined
): ActivityDetailStageKey {
  switch (orderStatus) {
    case 'received':
    case 'partially_received':
      return 'received';
    case 'out_for_delivery':
    case 'partially_out_for_delivery':
      return 'delivered';
    case 'processed':
    case 'partially_processed':
      return 'processed';
    default:
      return 'checkout';
  }
}

export function hasFulfillmentStageData(
  item: FulfillmentStageDataFields,
  stage: FulfillmentStage
): boolean {
  switch (stage) {
    case 'processed':
      return (
        hasSubtotalValue(item.processed_subtotal) ||
        hasStageUnitPriceSnapshot(item.processed_unit_price)
      );
    case 'delivered':
      return (
        hasSubtotalValue(item.delivered_subtotal) ||
        hasStageUnitPriceSnapshot(item.delivered_unit_price)
      );
    case 'received':
      return (
        hasSubtotalValue(item.received_subtotal) ||
        hasStageUnitPriceSnapshot(item.received_unit_price)
      );
  }
}

/** Whether SO/PGI/POD cells should render data or '-' for this order/item row. */
export function isActivityDetailFulfillmentStageVisible(
  orderStatus: string | undefined,
  stage: FulfillmentStage,
  item: FulfillmentStageDataFields
): boolean {
  if (orderStatus === 'cancelled') {
    return hasFulfillmentStageData(item, stage);
  }

  const maxStage = resolveMaxVisibleFulfillmentStage(orderStatus);
  return ACTIVITY_DETAIL_STAGE_ORDER[stage] <= ACTIVITY_DETAIL_STAGE_ORDER[maxStage];
}

/** Cancelled rows with null stage qty should render '-' instead of falling back to confirmed qty or 0. */
export function shouldShowFulfillmentStageQuantityPlaceholder(
  orderStatus: string | undefined,
  stageQty: number | null | undefined
): boolean {
  return orderStatus === 'cancelled' && stageQty == null;
}

export function resolveFulfillmentStageQuantityInput(
  orderStatus: string | undefined,
  stageQty: number | null | undefined,
  confirmedQuantity: number | null | undefined
): number | undefined {
  if (stageQty != null) {
    return stageQty;
  }

  if (orderStatus === 'cancelled') {
    return undefined;
  }

  return confirmedQuantity ?? undefined;
}

export function shouldShowCancelledNullPlaceholder(
  orderStatus: string | undefined,
  value: number | null | undefined
): boolean {
  return orderStatus === 'cancelled' && value == null;
}

export function resolveCheckoutQuantityInput(
  orderStatus: string | undefined,
  checkoutQty: number | null | undefined,
  initialQty: number | null | undefined,
  confirmedQty: number | null | undefined
): number | undefined {
  if (checkoutQty != null) {
    return checkoutQty;
  }

  if (orderStatus === 'cancelled') {
    return initialQty ?? undefined;
  }

  return confirmedQty ?? undefined;
}

export function shouldShowCheckoutQuantityPlaceholder(
  orderStatus: string | undefined,
  checkoutQty: number | null | undefined,
  initialQty: number | null | undefined
): boolean {
  return (
    orderStatus === 'cancelled' && checkoutQty == null && initialQty == null
  );
}

export function shouldShowCheckoutUnitPricePlaceholder(
  orderStatus: string | undefined,
  checkoutUnitPrice: number | null | undefined,
  initialPrice: number | null | undefined
): boolean {
  return (
    orderStatus === 'cancelled' &&
    checkoutUnitPrice == null &&
    initialPrice == null
  );
}

export function shouldShowCheckoutSubtotalPlaceholder(
  orderStatus: string | undefined,
  checkoutSubtotal: number | null | undefined,
  originalSubtotal: number | null | undefined
): boolean {
  return (
    orderStatus === 'cancelled' &&
    checkoutSubtotal == null &&
    originalSubtotal == null
  );
}

export function shouldShowFulfillmentStageUnitPricePlaceholder(
  orderStatus: string | undefined,
  stageUnitPrice: number | null | undefined
): boolean {
  return shouldShowCancelledNullPlaceholder(orderStatus, stageUnitPrice);
}

const FULFILLMENT_STAGE_UNIT_PRICE_FIELD = {
  processed: 'processed_unit_price',
  delivered: 'delivered_unit_price',
  received: 'received_unit_price',
} as const satisfies Record<
  FulfillmentStage,
  keyof Pick<
    OrderItemUnitPriceFields,
    'processed_unit_price' | 'delivered_unit_price' | 'received_unit_price'
  >
>;

const FULFILLMENT_STAGE_ORIGINAL_UNIT_PRICE_FIELD = {
  processed: 'processed_original_unit_price',
  delivered: 'delivered_original_unit_price',
  received: 'received_original_unit_price',
} as const satisfies Record<
  FulfillmentStage,
  keyof Pick<
    OrderItemUnitPriceFields,
    | 'processed_original_unit_price'
    | 'delivered_original_unit_price'
    | 'received_original_unit_price'
  >
>;

/**
 * Pre-discount list price for fulfillment-stage strikethrough rows.
 * Prefers the stage original-unit-price snapshot; legacy rows fall back to
 * latest_price because price_after_discount is recomputed from latest_price.
 */
export function resolveFulfillmentStageStrikethroughBase(
  item: Pick<
    OrderItemUnitPriceFields,
    | 'price'
    | 'latest_price'
    | 'processed_original_unit_price'
    | 'delivered_original_unit_price'
    | 'received_original_unit_price'
  >,
  stage: FulfillmentStage
): number {
  const originalField = FULFILLMENT_STAGE_ORIGINAL_UNIT_PRICE_FIELD[stage];

  return item[originalField] ?? item.latest_price ?? item.price ?? 0;
}

/**
 * Unit price for SO/PGI/POD columns — anchored to the stage snapshot when present.
 * Legacy rows fall back to buyer-aligned latest pricing (promo + voucher when enabled).
 */
export function resolveFulfillmentStageUnitPrice(
  item: OrderItemUnitPriceFields & ActivityItemPricingFields,
  stage: FulfillmentStage,
  isVoucherEnabled = false
): number {
  if (hasFreeGoods(item)) {
    return 0;
  }

  const unitPriceField = FULFILLMENT_STAGE_UNIT_PRICE_FIELD[stage];
  const snapshotPrice = item[unitPriceField];

  if (snapshotPrice != null) {
    return snapshotPrice;
  }

  if (isVoucherEnabled) {
    return getActivityOrderItemBuyerDisplay(item).unitPrice;
  }

  return getPromoAdjustedUnitPrice(item);
}

export function getVoucherAdjustedUnitPrice(
  promoAdjustedPrice: number,
  item: Pick<
    OrderItemUnitPriceFields,
    | 'has_voucher_discount'
    | 'voucher_discount_percentage'
    | 'voucher_discount_amount'
  >,
  isVoucherEnabled = false
): ReturnType<typeof calculateVoucherDiscount> {
  if (!isVoucherEnabled || !item.has_voucher_discount) {
    return {
      finalPrice: promoAdjustedPrice,
      originalPrice: promoAdjustedPrice,
    };
  }

  return calculateVoucherDiscount(promoAdjustedPrice, {
    hasVoucherDiscount: true,
    voucherDiscountPercentage: item.voucher_discount_percentage,
    voucherDiscountAmount: item.voucher_discount_amount,
  });
}

/** Unit price — buyer-aligned when voucher enabled; promo-only otherwise. */
export function getOrderItemCheckoutUnitPrice(
  item: OrderItemUnitPriceFields & ActivityItemPricingFields,
  isVoucherEnabled = false
): number {
  if (hasFreeGoods(item)) {
    return 0;
  }

  if (isVoucherEnabled) {
    return getActivityOrderItemBuyerDisplay(item).unitPrice;
  }

  return getPromoAdjustedUnitPrice(item);
}

/**
 * Unit price shown in Harga Akhir.
 * Promo/voucher lines reuse checkout unit price; plain rows keep API latest_price.
 */
export function getOrderItemDisplayLatestPrice(
  item: OrderItemUnitPriceFields & ActivityItemPricingFields,
  isVoucherEnabled = false
): number {
  if (hasFreeGoods(item)) {
    return 0;
  }

  const usesCheckoutPricing =
    Boolean(item.is_discount) ||
    (isVoucherEnabled && Boolean(item.has_voucher_discount));

  if (usesCheckoutPricing) {
    return getOrderItemCheckoutUnitPrice(item, isVoucherEnabled);
  }

  return item.latest_price ?? item.price ?? 0;
}

export function formatCurrency(amount: number): string {
  return buyerCurrencyFormatter(amount);
}

export interface ActivityImageObject {
  image?: string;
  thumbnail_url?: string;
  medium_image_url?: string;
  large_image_url?: string;
}

export type ItemCodeLookupFields = {
  quantity_free_goods?: number | null;
  free_goods_product_item_code?: number | null;
  product_item_code?: string | number | null;
  item_code?: number | string | null;
};

export function getItemCode(item: ItemCodeLookupFields): string {
  const convertToString = (
    value: string | number | undefined | null
  ): string => {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value);
  };

  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    const freeGoodsCode = convertToString(item.free_goods_product_item_code);
    if (freeGoodsCode) {
      return freeGoodsCode;
    }

    const productCode = convertToString(item.product_item_code);
    if (productCode) {
      return productCode;
    }

    const itemCode =
      'item_code' in item && item.item_code !== undefined
        ? convertToString(item.item_code)
        : '';
    if (itemCode) {
      return itemCode;
    }

    return '';
  }

  const productCode = convertToString(item.product_item_code);
  if (productCode) {
    return productCode;
  }

  const itemCode =
    'item_code' in item && item.item_code !== undefined
      ? convertToString(item.item_code)
      : '';
  if (itemCode) {
    return itemCode;
  }

  return '';
}

export function getImageUrl(item: BackofficeActivitiesOrderItem): string {
  const placeholder = 'assets/images/product_placeholder.png';

  const firstNonEmpty = (arr: unknown): string | undefined => {
    if (!Array.isArray(arr)) return undefined;
    return arr.find((url: string) => url);
  };

  // Handle array-based image fields (order items from API)
  const fromArrays =
    firstNonEmpty(item.images_medium) ||
    firstNonEmpty(item.images) ||
    firstNonEmpty(item.images_large) ||
    firstNonEmpty(item.image_thumbnails);

  if (fromArrays) return fromArrays;

  // Handle object-based image fields (promo/voucher free good items)
  const images = item.images;
  if (images && typeof images === 'object' && !Array.isArray(images)) {
    const { medium_image_url, image, large_image_url, thumbnail_url } =
      images as ActivityImageObject;
    return (
      medium_image_url ||
      image ||
      large_image_url ||
      thumbnail_url ||
      placeholder
    );
  }

  return placeholder;
}

export function isNewProduct(item: BackofficeActivitiesOrderItem): boolean {
  return Boolean(item.is_new_product);
}

export function hasFreeGoods(
  item: Pick<OrderItemSubtotalFields, 'quantity_free_goods'>
): boolean {
  return Boolean(
    item.quantity_free_goods && Number(item.quantity_free_goods) > 0
  );
}

export type ActivityItemDiffFields = {
  quantity?: number;
  confirmed_quantity?: number | null;
  price?: number;
  latest_price?: number | null;
  original_subtotal?: number | null;
  subtotal?: number | null;
  /** Present on buyer order detail; omitted on backoffice readActivityDetail. */
  is_stock_diff?: boolean;
  is_price_diff?: boolean;
  subtotal_diff?: number;
};

export type OrderItemSubtotalFields = {
  received_subtotal?: number | null;
  delivered_subtotal?: number | null;
  processed_subtotal?: number | null;
  subtotal?: number | null;
  saved_subtotal?: number | null;
  quantity_free_goods?: number | null;
  has_voucher_discount?: boolean;
  is_not_processed?: boolean;
};

type ActivityVoucherDiscountRow = {
  product_item_code: number;
  product_name: string;
  division: string;
  quantity: number;
  original_subtotal: number;
  discount_amount: number | null;
  discount_percent: number | null;
  saved_amount: number;
  final_subtotal: number;
  reward_type: 'DISCOUNT_PERCENT' | 'DISCOUNT_AMOUNT';
};

export type ActivityItemPricingFields = ActivityItemDiffFields &
  OrderItemSubtotalFields &
  OrderItemUnitPriceFields & {
    voucher_reward_type?: 'DISCOUNT_PERCENT' | 'DISCOUNT_AMOUNT';
    item_code?: number | string | null;
    title?: string | null;
    material_division?: string | null;
    voucher_discounts?: ActivityVoucherDiscountRow[];
  };

const toDisplayQuantity = (magnifiedQuantity: number | undefined): number =>
  Number(
    toDecimal(
      dinero({
        amount: Number(magnifiedQuantity ?? 0),
        currency: STOCK_UI,
      })
    )
  );

/** Build voucher_discounts[] for buyer pricing when API omits the array. */
export function resolveActivityVoucherDiscounts(
  item: ActivityItemPricingFields
): ActivityVoucherDiscountRow[] {
  if (
    Array.isArray(item.voucher_discounts) &&
    item.voucher_discounts.length > 0
  ) {
    return item.voucher_discounts;
  }

  if (!item.has_voucher_discount || !item.voucher_reward_type) {
    return [];
  }

  const rewardType = item.voucher_reward_type;
  const voucherQuantity = Number(item.confirmed_quantity ?? item.quantity ?? 0);
  const originalSubtotal = item.original_subtotal ?? item.subtotal ?? 0;
  const finalSubtotal = item.subtotal ?? 0;
  const computedSaved = Math.max(0, originalSubtotal - finalSubtotal);
  const savedAmount = item.saved_subtotal ?? computedSaved;

  let discountAmount: number | null = null;
  if (rewardType === 'DISCOUNT_AMOUNT') {
    discountAmount = item.voucher_discount_amount ?? null;
  } else if (rewardType === 'DISCOUNT_PERCENT') {
    const latestPrice = resolveOrderItemUnitPriceBase(item);
    const displayQty = toDisplayQuantity(voucherQuantity);
    const percent = item.voucher_discount_percentage;

    if (percent != null && latestPrice > 0 && displayQty > 0) {
      discountAmount = latestPrice * displayQty * (percent / 100);
    } else {
      discountAmount = computedSaved > 0 ? computedSaved : null;
    }
  }

  return [
    {
      product_item_code: Number(item.item_code ?? 0),
      product_name: item.title ?? '',
      division: item.material_division ?? '',
      quantity: voucherQuantity,
      original_subtotal: originalSubtotal,
      discount_amount: discountAmount,
      discount_percent:
        rewardType === 'DISCOUNT_PERCENT'
          ? item.voucher_discount_percentage ?? null
          : null,
      saved_amount: savedAmount,
      final_subtotal: finalSubtotal,
      reward_type: rewardType,
    },
  ];
}

const toDisplayQuantityForPromo = toDisplayQuantity;

function resolveActivityPromoPriceAfterDiscount(
  item: ActivityItemPricingFields
): number | null {
  if (!item.is_discount || item.price_after_discount == null) {
    return item.price_after_discount ?? null;
  }

  const displayQty = toDisplayQuantityForPromo(
    Number(item.confirmed_quantity ?? item.quantity ?? 0)
  );
  const subtotal = item.subtotal ?? 0;

  if (displayQty > 0 && subtotal > 0) {
    const impliedUnit = subtotal / displayQty;
    if (Math.abs(item.price_after_discount - impliedUnit) > 0.5) {
      return impliedUnit;
    }
  }

  return item.price_after_discount;
}

/** Checkout unit price — voucher % uses latest_price × (1 − percent/100). */
export function resolveActivityCheckoutUnitPrice(
  item: ActivityItemPricingFields
): number {
  if (hasFreeGoods(item)) {
    return 0;
  }

  const latestPrice = resolveOrderItemUnitPriceBase(item);

  if (
    item.has_voucher_discount &&
    item.voucher_reward_type === VOUCHER_REWARD_TYPE.DISCOUNT_PERCENT &&
    item.voucher_discount_percentage != null
  ) {
    return latestPrice * (1 - item.voucher_discount_percentage / 100);
  }

  if (
    item.has_voucher_discount &&
    item.voucher_reward_type === VOUCHER_REWARD_TYPE.DISCOUNT_AMOUNT &&
    item.voucher_discount_amount != null
  ) {
    return Math.max(0, latestPrice - item.voucher_discount_amount);
  }

  return getActivityOrderItemBuyerDisplay(item).unitPrice;
}

/**
 * True checkout-time unit price — always anchored to checkout data, never latest_price.
 * Used for the "Harga Checkout" column, which must show what the buyer saw at order time,
 * distinct from `resolveActivityCheckoutUnitPrice` (buyer-aligned latest price used elsewhere).
 *
 * Prefers the checkout_unit_price snapshot (already inclusive of promo/voucher discount)
 * when present. Legacy orders predating that snapshot fall back to item.price with the
 * promo discount applied — mirroring mobile's "Harga Awal" row (item_product_checkout.dart),
 * which applies is_discount/price_after_discount but never the voucher discount here.
 */
export function resolveActivityCheckoutOnlyUnitPrice(
  item: ActivityItemPricingFields
): number {
  if (hasFreeGoods(item)) {
    return 0;
  }

  if (item.checkout_unit_price != null) {
    return item.checkout_unit_price;
  }

  return getPromoAdjustedCheckoutUnitPrice(item);
}

export function adaptActivityItemForBuyerPricing(
  item: ActivityItemPricingFields | BackofficeActivitiesOrderItem
): ProductWithPromo {
  const promoPriceAfterDiscount = resolveActivityPromoPriceAfterDiscount(item);
  const compiled_discount =
    item.is_discount && promoPriceAfterDiscount != null
      ? [
          {
            is_discount: true,
            price_after_discount: promoPriceAfterDiscount,
          },
        ]
      : [];

  return {
    ...item,
    hasDiscount: Boolean(item.is_discount),
    compiled_discount,
    voucher_discounts: resolveActivityVoucherDiscounts(item),
  } as unknown as ProductWithPromo;
}

/** Buyer-aligned unit price and line subtotal for admin activity detail. */
export function getActivityOrderItemBuyerDisplay(
  item: ActivityItemPricingFields | BackofficeActivitiesOrderItem
) {
  return getOrderDetailProductDisplay(adaptActivityItemForBuyerPricing(item));
}

export function hasSubtotalValue(
  value: number | null | undefined
): value is number {
  return value != null && value !== 0;
}

export function isOrderItemSubtotalEmpty(
  item: OrderItemSubtotalFields
): boolean {
  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    return false;
  }

  return (
    !hasSubtotalValue(item.received_subtotal) &&
    !hasSubtotalValue(item.delivered_subtotal) &&
    !hasSubtotalValue(item.processed_subtotal) &&
    !hasSubtotalValue(item.subtotal)
  );
}

export function resolveOrderItemBaseSubtotal(
  item: Pick<
    OrderItemSubtotalFields,
    | 'received_subtotal'
    | 'delivered_subtotal'
    | 'processed_subtotal'
    | 'subtotal'
  >
): number {
  if (hasSubtotalValue(item.received_subtotal)) {
    return item.received_subtotal;
  }
  if (hasSubtotalValue(item.delivered_subtotal)) {
    return item.delivered_subtotal;
  }
  if (hasSubtotalValue(item.processed_subtotal)) {
    return item.processed_subtotal;
  }
  return item.subtotal ?? 0;
}

export function hasFulfillmentSubtotal(
  item: Pick<
    OrderItemSubtotalFields,
    'received_subtotal' | 'delivered_subtotal' | 'processed_subtotal'
  >
): boolean {
  return (
    hasSubtotalValue(item.received_subtotal) ||
    hasSubtotalValue(item.delivered_subtotal) ||
    hasSubtotalValue(item.processed_subtotal)
  );
}

/** Checkout subtotal only — reference for stage discrepancy dots, not line total display. */
export function getCheckoutSubtotal(
  item: OrderItemSubtotalFields
): number | null {
  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    return 0;
  }

  return hasSubtotalValue(item.subtotal) ? item.subtotal : null;
}

/** Checkout-time subtotal for admin display — null only when no snapshot exists. */
export function resolveActivityCheckoutDisplaySubtotal(
  item: Pick<OrderItemSubtotalFields, 'quantity_free_goods' | 'subtotal'> & {
    checkout_subtotal?: number | null;
    original_subtotal?: number | null;
  }
): number | null {
  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    return 0;
  }

  if (item.checkout_subtotal != null) {
    return item.checkout_subtotal;
  }

  if (item.subtotal != null && item.subtotal !== 0) {
    return item.subtotal;
  }

  if (item.original_subtotal != null) {
    return item.original_subtotal;
  }

  if (item.subtotal != null) {
    return item.subtotal;
  }

  return null;
}

export function shouldShowFulfillmentSubtotalDiscrepancy(
  checkoutSub: number | null | undefined,
  groupSub: number | null | undefined
): boolean {
  if (!hasSubtotalValue(groupSub)) {
    return false;
  }

  if (!hasSubtotalValue(checkoutSub)) {
    return true;
  }

  return groupSub !== checkoutSub;
}

/**
 * Canonical line subtotal — buyer-aligned via item.subtotal / voucher final_subtotal.
 */
export function getOrderItemDisplaySubtotal(
  item: ActivityItemPricingFields
): number {
  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    return 0;
  }

  return getActivityOrderItemBuyerDisplay(item).totalPrice;
}

export function isActivityItemChangePriceStock(
  item: Pick<
    ActivityItemDiffFields,
    | 'is_stock_diff'
    | 'is_price_diff'
    | 'subtotal_diff'
    | 'original_subtotal'
    | 'subtotal'
  >
): boolean {
  return (
    Boolean(item.is_stock_diff) ||
    (Boolean(item.is_price_diff) &&
      (item.subtotal_diff ?? 0) !== 0 &&
      (item.original_subtotal ?? 0) !== (item.subtotal ?? 0))
  );
}


/** Admin keeps checkout prices visible when buyer ordered qty > 0 but SAP confirmed 0. */
export function shouldShowAdminActivityPriceSection(
  item: Pick<
    ActivityItemPricingFields,
    | 'quantity'
    | 'price'
    | 'latest_price'
    | 'checkout_unit_price'
    | 'quantity_free_goods'
  >,
  buyerShowPriceSection: boolean
): boolean {
  if (buyerShowPriceSection) {
    return true;
  }

  if (item.quantity_free_goods && Number(item.quantity_free_goods) > 0) {
    return true;
  }

  const hasInitialOrderQty = (item.quantity ?? 0) > 0;
  const hasCheckoutUnitPrice =
    item.price != null ||
    item.latest_price != null ||
    item.checkout_unit_price != null;

  return hasInitialOrderQty && hasCheckoutUnitPrice;
}

export type ActivityItemDisplay = {
  checkoutQuantity: number;
  confirmedQuantity: number;
  initialUnitPrice: number;
  initialTotalPrice: number;
  unitPrice: number;
  lineSubtotal: number;
  showPriceSection: boolean;
  showInitialQuantity: boolean;
  showInitialUnitPrice: boolean;
  showInitialTotalPrice: boolean;
  isChangePriceStock: boolean;
};

/** Buyer-aligned diff flags and pricing for admin activity detail. */
export function getActivityItemDisplay(
  item: ActivityItemPricingFields | BackofficeActivitiesOrderItem
): ActivityItemDisplay {
  const buyerDisplay = getActivityOrderItemBuyerDisplay(item);

  return {
    checkoutQuantity: buyerDisplay.quantity,
    confirmedQuantity: buyerDisplay.confirmedQuantity,
    initialUnitPrice: buyerDisplay.initialUnitPrice,
    initialTotalPrice: buyerDisplay.initialTotalPrice,
    unitPrice: resolveActivityCheckoutUnitPrice(item),
    lineSubtotal: buyerDisplay.totalPrice,
    showPriceSection: shouldShowAdminActivityPriceSection(
      item,
      buyerDisplay.showPriceSection
    ),
    isChangePriceStock: buyerDisplay.isChangePriceStock,
    showInitialQuantity: buyerDisplay.showInitialQuantity,
    showInitialUnitPrice: buyerDisplay.showInitialUnitPrice,
    showInitialTotalPrice: buyerDisplay.showInitialTotalPrice,
  };
}

export function formatVoucherDiscount(discount: {
  voucher_discount_percentage?: number;
  voucher_discount_amount?: number;
}): string {
  if (discount.voucher_discount_percentage) {
    return `Disc. ${discount.voucher_discount_percentage}%`;
  }

  if (discount.voucher_discount_amount) {
    return `Disc. ${formatCurrency(discount.voucher_discount_amount)}`;
  }

  return '';
}

export function formatProductRewardImages(
  images: {
    image?: string;
    thumbnail_url?: string;
    medium_image_url?: string;
    large_image_url?: string;
  },
  defaultPlaceholder = 'assets/images/product_placeholder.png'
): {
  image: string;
  thumbnail_url: string;
  medium_image_url: string;
  large_image_url: string;
} {
  return {
    image: images.image || defaultPlaceholder,
    thumbnail_url: images.thumbnail_url || defaultPlaceholder,
    medium_image_url: images.medium_image_url || defaultPlaceholder,
    large_image_url: images.large_image_url || defaultPlaceholder,
  };
}

export function parseVoucherRuleProduct(ruleProduct: {
  is_rule_product: boolean;
  voucher_codes: string[];
  rules: Array<{
    voucher_code: string;
    voucher_name: string;
    min_quantity: number;
    reward_type: string;
  }>;
}): {
  is_rule_product: boolean;
  voucher_codes: string[];
  rules: Array<{
    voucher_code: string;
    voucher_name: string;
    min_quantity: number;
    reward_type: string;
  }>;
} {
  return {
    is_rule_product: ruleProduct.is_rule_product,
    voucher_codes: ruleProduct.voucher_codes || [],
    rules: ruleProduct.rules || [],
  };
}
